import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import {
  debounce,
  isDesktopBreakpoint,
  mediaQueryLarge,
  requestYieldCallback,
  resetImageFadeIn,
} from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    // Mark that a variant update is pending to prevent resets during fetch
    this.#variantUpdatePending = true;

    // Cancel any pending image reset since we're about to update
    this.resetVariant.cancel();
    this.#debouncedPreview.cancel();
    this.#pendingPreview = null;

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    // Clear the pending flag now that the update has arrived
    this.#variantUpdatePending = false;

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.#updateCardMedia(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Clear stored original image so next hover preview uses the new variant's image
    this.#originalImageSrc = null;
    this.#originalImageSrcset = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Updates the product URL from the fetched HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    if (!event.detail.data.html) return;

    const priceContainers = Array.from(this.querySelectorAll(`product-price [ref='priceContainer']`));
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainers.length > 0) {
      priceContainers.forEach((priceContainer) => {
        // replaceWith instead of morph — morph fails on structural changes (e.g., simple → sale price)
        priceContainer.replaceWith(newPriceElement.cloneNode(true));
      });
    }
  }

  /**
   * Updates the product card media (images) based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateCardMedia(event) {
    if (!event.detail.data.html) return;

    const currentContent = this.querySelector('.product-card-media__content');
    const newContent = event.detail.data.html.querySelector('.product-card-media__content');

    if (currentContent && newContent) {
      // Check the media mode from the figure element BEFORE morph
      const figure = /** @type {HTMLElement | null} */ (this.querySelector('.product-card-media[data-media-mode]'));
      const mediaMode = figure?.dataset.mediaMode;

      // Check if this is a sibling/product change (not just a variant change)
      const isSiblingUpdate = !!event.detail.data.newProduct;

      // Check if the main image is already showing a hover preview
      const currentMainImage = /** @type {HTMLImageElement | null} */ (
        currentContent.querySelector('.product-card-media__image')
      );
      const isShowingPreview = this.#originalImageSrc != null;

      // Carousel mode: only replace content for sibling swatches (different product)
      // For variant swatches, the carousel already has all images - no update needed
      if (mediaMode === 'carousel') {
        if (isSiblingUpdate) {
          this.#replaceCarouselContent(currentContent, newContent);
        }
        // For variant swatches in carousel mode, skip - carousel already has all images
      } else {
        // For hover or single mode, transform the new content before morph
        if (mediaMode === 'hover' || mediaMode === 'single') {
          this.#transformCarouselToHover(newContent, mediaMode === 'single');
        }

        // If the image is already showing from a hover preview, remove data-fade-in
        // from the new content so morph syncs this to the current element.
        // Without data-fade-in, resetImageFadeIn won't touch it and CSS default opacity (1) applies.
        if (isShowingPreview && currentMainImage) {
          const newMainImage = newContent.querySelector('.product-card-media__image');
          if (newMainImage) {
            const stripQuery = (/** @type {string} */ url) => url?.split('?')[0] || '';
            const currentPath = stripQuery(currentMainImage.src);
            const newPath = stripQuery(newMainImage.getAttribute('src') || '');

            if (currentPath === newPath) {
              newMainImage.removeAttribute('data-fade-in');
              newMainImage.setAttribute('data-loaded', 'true');
            }
          }
        }

        morph(currentContent, newContent);
      }

      // Reset fade-in animation for new images
      resetImageFadeIn(currentContent);
    }
  }

  /**
   * Transforms carousel HTML content to hover image format.
   * @param {Element} content - The content element containing carousel structure
   * @param {boolean} disableHover - Whether hover should be disabled (single mode)
   */
  #transformCarouselToHover(content, disableHover = false) {
    const carousel = content.querySelector('basic-carousel');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.splide__slide img');
    if (slides.length === 0) return;

    // Create hover format: base image + hover image (if not single mode)
    const baseImage = /** @type {HTMLImageElement} */ (slides[0]);
    const hoverImage = slides.length > 1 ? /** @type {HTMLImageElement} */ (slides[1]) : null;

    // Build new content
    const fragment = document.createDocumentFragment();

    // Clone and setup base image
    const newBaseImage = /** @type {HTMLImageElement} */ (baseImage.cloneNode(true));
    newBaseImage.className = 'product-card-media__image';
    newBaseImage.setAttribute('data-fade-in', '');
    newBaseImage.setAttribute('onload', "this.setAttribute('data-loaded', 'true')");
    fragment.appendChild(newBaseImage);

    // Clone and setup hover image (if exists and not single mode)
    if (hoverImage && !disableHover) {
      const newHoverImage = /** @type {HTMLImageElement} */ (hoverImage.cloneNode(true));
      newHoverImage.className = 'product-card-media__hover-image';
      newHoverImage.setAttribute('data-fade-in', '');
      newHoverImage.setAttribute('onload', "this.setAttribute('data-loaded', 'true')");
      fragment.appendChild(newHoverImage);
    }

    // Replace carousel with hover format
    content.innerHTML = '';
    content.appendChild(fragment);
  }

  /**
   * Replaces carousel content without flicker by inserting new content before removing old.
   * @param {Element} currentContent - The current content container
   * @param {Element} newContent - The new content to insert
   */
  #replaceCarouselContent(currentContent, newContent) {
    const existingCarousel = /** @type {HTMLElement & { disconnectedCallback?: () => void }} */ (
      currentContent.querySelector('basic-carousel')
    );

    // Create new content from template (ensures custom elements initialize correctly)
    const template = document.createElement('template');
    template.innerHTML = newContent.innerHTML;

    // Pre-mark first slide image as loaded to prevent opacity flash
    const firstImage = /** @type {HTMLImageElement | null} */ (
      template.content.querySelector('.splide__slide img')
    );
    if (firstImage) {
      firstImage.removeAttribute('data-fade-in');
      firstImage.setAttribute('data-loaded', 'true');
    }

    // Insert new carousel before removing old (prevents empty flash)
    currentContent.appendChild(template.content.cloneNode(true));

    // Clean up and remove old carousel after new one is in DOM
    existingCarousel?.disconnectedCallback?.();
    existingCarousel?.remove();
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {string | null} */
  #originalImageSrc = null;

  /** @type {string | null} */
  #originalImageSrcset = null;

  /** @type {boolean} */
  #variantUpdatePending = false;

  /** @type {boolean} */
  #swatchImagesPreloaded = false;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Gets the main product card image element.
   * @returns {HTMLImageElement | null}
   */
  #getMainImage() {
    return this.querySelector('.product-card-media__image');
  }

  /**
   * Applies loading transition state to an image and executes callback.
   * Handles fade transition by toggling data-preview-loading attribute.
   * @param {HTMLImageElement} image - The image element
   * @param {() => void} swapCallback - Callback that performs the image src swap
   */
  #swapImageWithTransition(image, swapCallback) {
    const hadDataLoaded = image.hasAttribute('data-loaded');
    image.removeAttribute('data-loaded');
    image.setAttribute('data-preview-loading', '');

    const removeLoading = () => {
      image.removeAttribute('data-preview-loading');
      if (hadDataLoaded) image.setAttribute('data-loaded', '');
      image.removeEventListener('load', removeLoading);
    };

    image.addEventListener('load', removeLoading);
    swapCallback();

    // Handle cached images (load event won't fire)
    if (image.complete) removeLoading();
  }

  /** @type {{ target: HTMLElement } | null} */
  #pendingPreview = null;

  /**
   * Previews a variant by switching to its image.
   * @param {string | PointerEvent} idOrEvent - The id of the media to preview, or the pointer event if no id.
   * @param {PointerEvent} [maybeEvent] - The pointer event when id is provided.
   */
  previewVariant(idOrEvent, maybeEvent) {
    // Handle both signatures: previewVariant(id, event) and previewVariant(event)
    const id = idOrEvent instanceof Event ? null : idOrEvent;
    const event = /** @type {PointerEvent} */ (idOrEvent instanceof Event ? idOrEvent : maybeEvent);

    // Only work on desktop with mouse
    if (!isDesktopBreakpoint() || event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    // If we have a slideshow and an id, use it immediately (no debounce needed)
    const { slideshow } = this.refs;
    if (slideshow && id) {
      slideshow.select({ id }, undefined, { animate: false });
      return;
    }

    // Store pending preview and trigger debounced image swap
    this.#pendingPreview = { target: /** @type {HTMLElement} */ (event.target) };
    this.#debouncedPreview();
  }

  /**
   * Preloads all swatch preview images so hover swaps are instant.
   * Called once on first pointer enter of the swatch container.
   */
  preloadSwatchImages() {
    if (this.#swatchImagesPreloaded) return;
    this.#swatchImagesPreloaded = true;

    const urls = new Set();
    for (const el of this.querySelectorAll('[data-preview-image-url]')) {
      const url = /** @type {HTMLElement} */ (el).dataset.previewImageUrl;
      if (url) urls.add(url);
    }

    for (const url of urls) {
      const img = new Image();
      img.src = url;
    }
  }

  #debouncedPreview = debounce(() => {
    if (!this.#pendingPreview) return;

    const previewImageUrl = this.#pendingPreview.target.dataset.previewImageUrl;
    if (!previewImageUrl) return;

    const mainImage = this.#getMainImage();
    if (!mainImage) return;

    // If we're in carousel mode, reset to first slide so the preview is visible
    const carousel = /** @type {HTMLElement & { goTo?: (index: number) => void }} */ (
      this.querySelector('basic-carousel.product-card-carousel')
    );
    carousel?.goTo?.(0);

    // Store original src and srcset if not already stored
    if (!this.#originalImageSrc) {
      this.#originalImageSrc = mainImage.src;
      this.#originalImageSrcset = mainImage.srcset;
    }

    this.#swapImageWithTransition(mainImage, () => {
      mainImage.srcset = '';
      mainImage.src = previewImageUrl;
    });
  }, 100);

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    // Don't reset if a variant update is pending (prevents flicker during fetch)
    if (this.#variantUpdatePending) return;

    // Cancel any pending preview
    this.#debouncedPreview.cancel();
    this.#pendingPreview = null;

    const { slideshow } = this.refs;

    // Handle non-slideshow case: restore original image src and srcset
    if (!slideshow) {
      if (this.#originalImageSrc) {
        const mainImage = this.#getMainImage();
        if (mainImage) {
          const originalSrc = this.#originalImageSrc;
          const originalSrcset = this.#originalImageSrcset;

          this.#swapImageWithTransition(mainImage, () => {
            mainImage.src = originalSrc;
            if (originalSrcset) {
              mainImage.srcset = originalSrcset;
            }
          });
        }
        this.#originalImageSrc = null;
        this.#originalImageSrcset = null;
      }

      // Reset carousel to first slide if in carousel mode
      const carousel = /** @type {HTMLElement & { goTo?: (index: number) => void }} */ (
        this.querySelector('basic-carousel.product-card-carousel')
      );
      carousel?.goTo?.(0);

      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives,
   * and sibling swatches that switch to different products.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const clickedSwatch = event.target;
    // Check if this is a sibling swatch (has connected-product-url pointing to different product)
    const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl;
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const isSiblingSwatch = connectedProductUrl && connectedProductUrl !== currentProductUrl;

    if (isSiblingSwatch) {
      // Sibling swatch: fetch the sibling product WITHOUT any variant/option params.
      // This lets Shopify use its default selection (selected_or_first_available_variant)
      event.stopPropagation();

      this.updateSelectedOption(clickedSwatch);

      // Dispatch event to notify product card (cancels pending image resets)
      this.dispatchEvent(new VariantSelectedEvent({ id: clickedSwatch.dataset.optionValueId ?? '' }));

      const url = new URL(connectedProductUrl, window.location.origin);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      // Store the variant ID for URL update after morph (for product card link)
      const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;
      if (firstAvailableVariantId) {
        this.pendingVariantId = firstAvailableVariantId;
      }

      this.fetchUpdatedSection(url.href);
      return;
    }

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
